import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GroupStanding {
  position: number;
  team: string;
  wins: number;
  losses: number;
  gamesFor: number;
  gamesAgainst: number;
  points: number;
  isUser: boolean;
  // Campos adicionales para formato admin
  matchesPlayed: number;
  setsWon: number;
  setsLost: number;
  player1: string;
  player2: string;
}

/**
 * Normaliza category y subcategory para que coincidan con el formato de registrations.
 * Si subcategory viene vacío pero category tiene espacios (ej: "Mixto B"), 
 * separa el último token como subcategoría.
 * Ejemplos:
 *   - ("Mixto B", undefined) → { category: "Mixto", subcategory: "B" }
 *   - ("Masculino 1era", undefined) → { category: "Masculino", subcategory: "1era" }
 *   - ("Mixto", "B") → { category: "Mixto", subcategory: "B" }
 */
function normalizeCategorySubcategory(inputCategory: string, inputSubcategory?: string): { 
  category: string; 
  subcategory: string | undefined 
} {
  // Si ya viene subcategory explícita, usarla tal cual
  if (inputSubcategory && inputSubcategory.trim()) {
    return { 
      category: inputCategory?.trim() || '', 
      subcategory: inputSubcategory.trim() 
    };
  }
  
  // Si category tiene espacios, separar el último token como subcategory
  const trimmedCategory = inputCategory?.trim() || '';
  const parts = trimmedCategory.split(' ');
  
  if (parts.length >= 2) {
    const subcategory = parts.pop()!; // Último token
    const category = parts.join(' '); // El resto
    
    console.log('[useGroupStandings] 🔄 Normalización aplicada:', {
      input: { category: inputCategory, subcategory: inputSubcategory },
      output: { category, subcategory }
    });
    
    return { category, subcategory };
  }
  
  // No hay espacios, devolver tal cual
  return { category: trimmedCategory, subcategory: undefined };
}

export function useGroupStandings(eventId: string, groupName: string, category: string, subcategory?: string) {
  const [standings, setStandings] = useState<GroupStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStandings = async () => {
    if (!eventId || !groupName) {
      setStandings([]);
      setLoading(false);
      return;
    }
    
    // Normalizar category/subcategory para coincidir con formato de registrations
    const normalized = normalizeCategorySubcategory(category, subcategory);
    console.log('[useGroupStandings] 📊 Parámetros de consulta:', {
      eventId,
      groupName,
      inputCategory: category,
      inputSubcategory: subcategory,
      normalizedCategory: normalized.category,
      normalizedSubcategory: normalized.subcategory
    });
    
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      // Consultar reglas del evento para saber si tiene super tie-break
      const { data: eventData } = await supabase
        .from('events')
        .select('rules, tournament_format, sport')
        .eq('id', eventId)
        .single();

      let hasSuperTieBreak = false;
      if (eventData?.rules) {
        try {
          const parsed = typeof eventData.rules === 'string' ? JSON.parse(eventData.rules) : eventData.rules;
          hasSuperTieBreak = parsed?.groupRules?.superTieBreak === true;
        } catch (e) {}
      }
      const tournamentFormat = eventData?.tournament_format;
      const sport = (eventData?.sport || '').toLowerCase();
      const pointsPerWin = (sport === 'padel' && tournamentFormat !== 'elimination') ? 3 : 2;
      console.log('[useGroupStandings] Super tie-break habilitado:', hasSuperTieBreak, '| Formato:', tournamentFormat, '| Sport:', sport, '| PointsPerWin:', pointsPerWin);

      // SIEMPRE obtener registrations del grupo para mapear equipos → user_ids
      let regQuery = supabase
        .from('registrations')
        .select('player1_name, player2_name, user_id, partner_user_id')
        .eq('event_id', eventId)
        .eq('group', groupName)
        .eq('status', 'confirmed');
      
      // Filtrar por categoría y subcategoría NORMALIZADAS
      if (normalized.category) {
        regQuery = regQuery.eq('category', normalized.category);
      }
      if (normalized.subcategory) {
        regQuery = regQuery.eq('subcategory', normalized.subcategory);
      }
      
      const { data: groupRegistrations } = await regQuery;

      // Crear mapa de nombre de equipo → {user_id, partner_user_id}
      const teamUserMap: { [teamName: string]: { userId: string | null, partnerId: string | null } } = {};
      groupRegistrations?.forEach(reg => {
        const p1 = reg.player1_name || '';
        const p2 = reg.player2_name || '';
        const teamName = p2 ? `${p1} / ${p2}` : p1;
        teamUserMap[teamName] = {
          userId: reg.user_id,
          partnerId: reg.partner_user_id
        };
      });

      // Función para verificar si el usuario actual pertenece al equipo (por ID, no por nombre)
      const isUserInTeam = (teamName: string): boolean => {
        if (!user?.id) return false;
        const teamInfo = teamUserMap[teamName];
        if (!teamInfo) return false;
        return teamInfo.userId === user.id || teamInfo.partnerId === user.id;
      };

      console.log('[useGroupStandings] Team user map:', teamUserMap);

      // Obtener resultados de partidos del grupo
      let matchQuery = supabase
        .from('tournament_match_results')
        .select('*')
        .eq('tournament_id', eventId)
        .eq('group_name', groupName)
        .not('winner_team', 'is', null);
      
      // Filtrar por categoría y subcategoría NORMALIZADAS
      if (normalized.category) {
        matchQuery = matchQuery.eq('category', normalized.category);
      }
      if (normalized.subcategory) {
        matchQuery = matchQuery.eq('subcategory', normalized.subcategory);
      }
      
      const { data: matches, error: matchError } = await matchQuery;
      
      // Filtrar partidos de fase de grupos
      const groupPhaseMatches = matches?.filter(m => 
        m.phase?.toLowerCase().includes('grupo')
      ) || [];
      
      console.log('[useGroupStandings] Matches query result:', {
        eventId,
        groupName,
        totalMatches: matches?.length,
        groupPhaseMatchesCount: groupPhaseMatches.length,
        allPhases: matches?.map(m => m.phase),
        matches: groupPhaseMatches.map(m => ({
          team1: `${m.team1_player1} / ${m.team1_player2}`,
          team2: `${m.team2_player1} / ${m.team2_player2}`,
          winner: m.winner_team,
          phase: m.phase
        }))
      });

      if (matchError) throw matchError;
      
      const filteredMatches = groupPhaseMatches;

      // Si no hay resultados de partidos de fase de grupos, obtener equipos desde registrations (nombres reales)
      if (!filteredMatches || filteredMatches.length === 0) {
        console.log('[useGroupStandings] No hay resultados de grupos, obteniendo equipos de registrations');
        
        // Obtener registraciones confirmadas del grupo
        let fallbackQuery = supabase
          .from('registrations')
          .select('id, player1_name, player2_name, user_id, partner_user_id, team_order')
          .eq('event_id', eventId)
          .eq('group', groupName)
          .eq('status', 'confirmed');
        
        // Filtrar por categoría y subcategoría NORMALIZADAS
        if (normalized.category) {
          fallbackQuery = fallbackQuery.eq('category', normalized.category);
        }
        if (normalized.subcategory) {
          fallbackQuery = fallbackQuery.eq('subcategory', normalized.subcategory);
        }
        
        const { data: groupRegistrations, error: regError } = await fallbackQuery
          .order('team_order', { ascending: true, nullsFirst: false });

        if (regError) {
          console.error('[useGroupStandings] Error obteniendo registrations:', regError);
          throw regError;
        }

        if (!groupRegistrations || groupRegistrations.length === 0) {
          console.log('[useGroupStandings] No se encontraron registraciones en el grupo');
          setStandings([]);
          setLoading(false);
          return;
        }

        // Crear clasificación inicial con nombres reales de registrations
        const initialStandings = groupRegistrations.map((reg, index) => {
          const p1 = reg.player1_name || 'Por asignar';
          const p2 = reg.player2_name || '';
          const teamName = p2 ? `${p1} / ${p2}` : p1;
          
          // Determinar si es el usuario actual (por user_id o partner_user_id)
          const isUser = reg.user_id === user?.id || reg.partner_user_id === user?.id;

          return {
            position: index + 1,
            team: teamName,
            wins: 0,
            losses: 0,
            gamesFor: 0,
            gamesAgainst: 0,
            points: 0,
            isUser,
            matchesPlayed: 0,
            setsWon: 0,
            setsLost: 0,
            player1: p1,
            player2: p2
          };
        });

        console.log('[useGroupStandings] Clasificación inicial con nombres reales:', initialStandings);
        setStandings(initialStandings);
        setLoading(false);
        return;
      }

      // Calcular estadísticas por equipo
      const teamStats: { [teamKey: string]: GroupStanding } = {};

      filteredMatches.forEach(match => {
        const t1p1 = match.team1_player1 || '';
        const t1p2 = match.team1_player2 || '';
        const t2p1 = match.team2_player1 || '';
        const t2p2 = match.team2_player2 || '';
        
        const team1Name = t1p2 ? `${t1p1} / ${t1p2}` : t1p1;
        const team2Name = t2p2 ? `${t2p1} / ${t2p2}` : t2p1;

        // Inicializar equipos si no existen
        if (!teamStats[team1Name]) {
          teamStats[team1Name] = {
            position: 0,
            team: team1Name,
            wins: 0,
            losses: 0,
            gamesFor: 0,
            gamesAgainst: 0,
            points: 0,
            isUser: isUserInTeam(team1Name),
            matchesPlayed: 0,
            setsWon: 0,
            setsLost: 0,
            player1: t1p1,
            player2: t1p2
          };
        }

        if (!teamStats[team2Name]) {
          teamStats[team2Name] = {
            position: 0,
            team: team2Name,
            wins: 0,
            losses: 0,
            gamesFor: 0,
            gamesAgainst: 0,
            points: 0,
            isUser: isUserInTeam(team2Name),
            matchesPlayed: 0,
            setsWon: 0,
            setsLost: 0,
            player1: t2p1,
            player2: t2p2
          };
        }

        // Calcular juegos y sets a favor/contra desde los sets
        let team1Games = 0;
        let team2Games = 0;
        let team1SetsWon = 0;
        let team2SetsWon = 0;

        if (match.sets && Array.isArray(match.sets)) {
          const setsArray = match.sets as any[];
          setsArray.forEach((set: any, setIndex: number) => {
            const t1 = set.team1 || 0;
            const t2 = set.team2 || 0;
            
            // Contar sets ganados (siempre, incluyendo super tie-break)
            if (t1 > t2) team1SetsWon++;
            else if (t2 > t1) team2SetsWon++;
            
            // Contar juegos: excluir el 3er set si es super tie-break
            const isSuperTB = hasSuperTieBreak 
              && setsArray.length === 3 
              && setIndex === 2;
            if (!isSuperTB) {
              team1Games += t1;
              team2Games += t2;
            }
          });
        }

        // Actualizar estadísticas de juegos
        teamStats[team1Name].gamesFor += team1Games;
        teamStats[team1Name].gamesAgainst += team2Games;
        teamStats[team2Name].gamesFor += team2Games;
        teamStats[team2Name].gamesAgainst += team1Games;

        // Actualizar estadísticas de sets
        teamStats[team1Name].setsWon += team1SetsWon;
        teamStats[team1Name].setsLost += team2SetsWon;
        teamStats[team2Name].setsWon += team2SetsWon;
        teamStats[team2Name].setsLost += team1SetsWon;

        // Incrementar partidos jugados
        teamStats[team1Name].matchesPlayed++;
        teamStats[team2Name].matchesPlayed++;

        // Asignar victoria/derrota
        if (match.winner_team === 1) {
          teamStats[team1Name].wins++;
          teamStats[team2Name].losses++;
        } else if (match.winner_team === 2) {
          teamStats[team2Name].wins++;
          teamStats[team1Name].losses++;
        }
      });

      // Calcular puntos y ordenar
      const standingsArray = Object.values(teamStats)
        .map(team => ({
          ...team,
          points: team.wins * pointsPerWin // Pádel round-robin: 3 pts/victoria; resto: 2
        }))
        .sort((a, b) => {
          if (tournamentFormat === 'elimination') {
            // Relámpago: 1) Diferencia de juegos, 2) Puntos, 3) Juegos a favor
            const gameDiffA = a.gamesFor - a.gamesAgainst;
            const gameDiffB = b.gamesFor - b.gamesAgainst;
            if (gameDiffB !== gameDiffA) return gameDiffB - gameDiffA;
            if (b.points !== a.points) return b.points - a.points;
            return b.gamesFor - a.gamesFor;
          }
          // Round-robin/Circuito: 1) Puntos, 2) Diferencia de juegos, 3) Juegos a favor, 4) Diferencia de sets
          if (b.points !== a.points) return b.points - a.points;
          
          const gameDiffA = a.gamesFor - a.gamesAgainst;
          const gameDiffB = b.gamesFor - b.gamesAgainst;
          if (gameDiffB !== gameDiffA) return gameDiffB - gameDiffA;
          
          if (b.gamesFor !== a.gamesFor) return b.gamesFor - a.gamesFor;
          
          const setDiffA = a.setsWon - a.setsLost;
          const setDiffB = b.setsWon - b.setsLost;
          return setDiffB - setDiffA;
        })
        .map((team, index) => ({
          ...team,
          position: index + 1
        }));

      setStandings(standingsArray);
    } catch (err: any) {
      console.error('[useGroupStandings] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStandings();

    // Suscripción en tiempo real
    const channel = supabase
      .channel(`group-standings-${eventId}-${groupName}`)
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'tournament_match_results',
          filter: `tournament_id=eq.${eventId}`
        }, 
        () => {
          console.log('[useGroupStandings] Resultados actualizados, recargando...');
          fetchStandings();
        }
      )
      .subscribe();

    const pollInterval = setInterval(() => {
      fetchStandings();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId, groupName, category, subcategory]);

  return { 
    standings, 
    loading, 
    error, 
    refetch: fetchStandings 
  };
}
